/**
 * Smart favicon resolver that tries multiple sources:
 * 1. Direct /favicon.ico request
 * 2. Parse page HTML for meta tags (og:image, twitter:image, icon links)
 * 3. Favicon API service (as fallback)
 * 4. Local fallback icon
 */

const FAVICON_CACHE = new Map();
const FAVICON_TIMEOUT = 5000; // 5 second timeout for fetches

/**
 * Extracts favicon URL from page HTML
 * Looks for: meta tags (og:image, twitter:image), link rel="icon"
 */
function extractFaviconFromHTML(html, origin) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Priority order for favicon sources
    const selectors = [
      'link[rel="icon"][href]',
      'link[rel="shortcut icon"][href]',
      'meta[property="og:image"][content]',
      'meta[name="twitter:image"][content]',
      'link[rel="apple-touch-icon"][href]',
    ];

    for (const selector of selectors) {
      const el = doc.querySelector(selector);
      if (el) {
        const attr = el.getAttribute("href") || el.getAttribute("content");
        if (attr) {
          // Resolve relative URLs
          if (attr.startsWith("http")) return attr;
          if (attr.startsWith("//")) return `https:${attr}`;
          if (attr.startsWith("/")) return `${origin}${attr}`;
          return `${origin}/${attr}`;
        }
      }
    }
  } catch (e) {
    // Parsing error, continue to fallback
  }
  return null;
}

/**
 * Fetches favicon for a given origin
 * Returns favicon URL or null if not found
 */
async function resolveFaviconForOrigin(origin) {
  if (!origin) return null;

  // Check cache first
  if (FAVICON_CACHE.has(origin)) {
    return FAVICON_CACHE.get(origin);
  }

  // Try direct /favicon.ico first (fastest)
  try {
    const headResponse = await Promise.race([
      fetch(`${origin}/favicon.ico`, { method: "HEAD", mode: "no-cors" }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), FAVICON_TIMEOUT)
      ),
    ]);
    if (headResponse.status !== 404) {
      const faviconUrl = `${origin}/favicon.ico`;
      FAVICON_CACHE.set(origin, faviconUrl);
      return faviconUrl;
    }
  } catch (e) {
    // HEAD request failed or timed out, try parsing HTML
  }

  // Try fetching the page HTML to extract favicon from meta tags
  try {
    const response = await Promise.race([
      fetch(origin, { mode: "cors" }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), FAVICON_TIMEOUT)
      ),
    ]);

    if (response.ok) {
      const html = await response.text();
      const faviconUrl = extractFaviconFromHTML(html, origin);
      if (faviconUrl) {
        FAVICON_CACHE.set(origin, faviconUrl);
        return faviconUrl;
      }
    }
  } catch (e) {
    // CORS error or timeout, continue to fallback
  }

  // Use favicon API service as backup (returns CDN-hosted favicon)
  // Using DuckDuckGo's favicon service which has good coverage
  const apiUrl = `https://icons.duckduckgo.com/ip3/${new URL(origin).hostname}.ico`;
  FAVICON_CACHE.set(origin, apiUrl);
  return apiUrl;
}

/**
 * Get favicon URL with fallback chain
 * @param {string} originalUrl - The target URL
 * @returns {Promise<string>} - Favicon URL or fallback
 */
export async function getFaviconUrl(originalUrl, fallbackIcon = "/browser-icon.svg") {
  try {
    const origin = new URL(originalUrl).origin;
    const faviconUrl = await resolveFaviconForOrigin(origin);
    return faviconUrl || fallbackIcon;
  } catch (e) {
    return fallbackIcon;
  }
}

/**
 * Get favicon URL synchronously with immediate fallback
 * Triggers async resolution in background for cache population
 * @param {string} originalUrl - The target URL
 * @param {string} fallbackIcon - Fallback icon URL
 * @returns {string} - Favicon URL or fallback (may be fallback initially)
 */
export function getFaviconUrlSync(originalUrl, fallbackIcon = "/browser-icon.svg") {
  try {
    const origin = new URL(originalUrl).origin;

    // Check cache first
    if (FAVICON_CACHE.has(origin)) {
      return FAVICON_CACHE.get(origin);
    }

    // Return immediate fallback, but resolve in background
    // This keeps the UI fast while still improving favicon accuracy over time
    resolveFaviconForOrigin(origin).catch(() => {});
    
    // Return optimistic direct favicon URL (will be replaced by resolver)
    return `${origin}/favicon.ico`;
  } catch (e) {
    return fallbackIcon;
  }
}

/**
 * Pre-warm favicon cache for multiple URLs
 * Useful for batch loading
 */
export async function prewarmFaviconCache(urls) {
  const promises = urls.map((url) => {
    try {
      const origin = new URL(url).origin;
      if (!FAVICON_CACHE.has(origin)) {
        return resolveFaviconForOrigin(origin);
      }
    } catch (e) {}
    return null;
  });

  await Promise.allSettled(promises);
}

/**
 * Clear favicon cache
 * Useful for manual refresh or when cache grows too large
 */
export function clearFaviconCache() {
  FAVICON_CACHE.clear();
}
